import { Volume2, VolumeX, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";

interface Voice {
  voice: SpeechSynthesisVoice;
  name: string;
  lang: string;
  localName: string;
}

interface TTSControlsProps {
  voices: Voice[];
  selectedVoice: SpeechSynthesisVoice | null;
  onVoiceChange: (voice: SpeechSynthesisVoice) => void;
  rate: number;
  onRateChange: (rate: number) => void;
  pitch: number;
  onPitchChange: (pitch: number) => void;
  isSpeaking: boolean;
  onStop: () => void;
}

const TTSControls = ({
  voices,
  selectedVoice,
  onVoiceChange,
  rate,
  onRateChange,
  pitch,
  onPitchChange,
  isSpeaking,
  onStop,
}: TTSControlsProps) => {
  // Group voices by language
  const groupedVoices = voices.reduce((acc, voice) => {
    const langCode = voice.lang.split("-")[0];
    const langNames: Record<string, string> = {
      ar: "العربية",
      en: "English",
      fr: "Français",
      de: "Deutsch",
      es: "Español",
      it: "Italiano",
      ja: "日本語",
      ko: "한국어",
      zh: "中文",
      ru: "Русский",
      pt: "Português",
      tr: "Türkçe",
      nl: "Nederlands",
      pl: "Polski",
      hi: "हिन्दी",
    };
    const langName = langNames[langCode] || langCode.toUpperCase();
    
    if (!acc[langName]) {
      acc[langName] = [];
    }
    acc[langName].push(voice);
    return acc;
  }, {} as Record<string, Voice[]>);

  return (
    <div className="flex items-center gap-2">
      {isSpeaking && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onStop}
          className="text-destructive hover:text-destructive"
          title="إيقاف التحدث"
        >
          <VolumeX className="w-4 h-4" />
        </Button>
      )}
      
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" title="إعدادات الصوت">
            <Settings2 className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Volume2 className="w-4 h-4 text-primary" />
              <h4 className="font-medium">إعدادات الصوت</h4>
            </div>

            <div className="space-y-2">
              <Label>اختر الصوت</Label>
              <Select
                value={selectedVoice?.name || ""}
                onValueChange={(name) => {
                  const voice = voices.find((v) => v.name === name);
                  if (voice) onVoiceChange(voice.voice);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر صوتاً" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {Object.entries(groupedVoices).map(([lang, langVoices]) => (
                    <div key={lang}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                        {lang}
                      </div>
                      {langVoices.map((voice) => (
                        <SelectItem key={voice.name} value={voice.name}>
                          {voice.localName}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>السرعة</Label>
                <span className="text-xs text-muted-foreground">{rate.toFixed(1)}x</span>
              </div>
              <Slider
                value={[rate]}
                onValueChange={([value]) => onRateChange(value)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>النبرة</Label>
                <span className="text-xs text-muted-foreground">{pitch.toFixed(1)}</span>
              </div>
              <Slider
                value={[pitch]}
                onValueChange={([value]) => onPitchChange(value)}
                min={0.5}
                max={2}
                step={0.1}
                className="w-full"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              يستخدم تقنية Web Speech API المدمجة في المتصفح
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TTSControls;
